import { Check } from '@mui/icons-material';
import { Button } from '@mui/material';
import { getAuth } from 'firebase/auth';
import React, { FC, useEffect, useState } from 'react';
import { handleError } from '../../../common/firebase/auth-error.service';
import { FirestoreService } from '../../../common/firebase/firestore.service';
import { ServiceManager } from '../../../services/service-manager';
import { explanationIds, Ftue, UserData } from '../../../common/authentication/models/user';
import Bubble from '../Bubble/Bubble';
import { finishCurrentStep, getAnchorEl, getCurrentExplanation, getCurrentStep } from './explanations-manager';
import styles from './Explanations.module.css';
import { Explanation, ExplanationStep } from './models/explanations';

interface ExplanationsProps {
    showedExplanation: { id: explanationIds },
    user: UserData,
    setUser: (user: UserData) => void,
    ftue: Ftue,
    setFtue: (ftue: Ftue) => void
}

const Explanations: FC<ExplanationsProps> = ({ user, setUser, ftue, setFtue, showedExplanation }) => {

    const [explanations, setExplanations] = useState<Explanation[]>([
        {
            name: 'all-explanations',
            steps: [
                {
                    title: 'Weergave bewerken',
                    explanation: 'Hier kunt u aspecten van de 3D viewer aan- en uitzetten. Ook kunt u specifieke objecten verbergen door ze aan te klikken.',
                    placement: 'left-end',
                    id: explanationIds.initialDisplay,
                    finished: false,
                    autoNextId: explanationIds.initialSelectSite
                },
                {
                    title: 'Kavel bewerken',
                    explanation: `Hier kunt u uw kavel selecteren of zelf intekenen. Ook kunt u een onderlegger toevoegen via “achtergrond inladen”`,
                    placement: 'left-end',
                    id: explanationIds.initialSelectSite,
                    finished: false,
                    autoNextId: explanationIds.buildingAdd,
                },
                {
                    title: 'Kavel selecteren',
                    anchorId: 'moos-logo',
                    placement: 'bottom-start',
                    explanation: `Klik op de kavels die u wilt gebruiken als perceel voor uw gebouwen.`,
                    id: explanationIds.siteSelectionPick,
                    finished: false
                },
                {
                    title: 'Kavel goedkeuren',
                    explanation: `Hier keurt u de selectie goed en bekijkt u de kadastrale gegevens van de verschillende percelen.`,
                    placement: 'left-end',
                    id: explanationIds.siteSelectionData,
                    finished: false,
                },
                {
                    title: 'Kavelgrens tekenen',
                    explanation: `Teken de hoekpunten van het kavel met uw muis, vinger of pen.`,
                    placement: 'bottom-start',
                    anchorId: 'moos-logo',
                    id: explanationIds.siteDrawDraw,
                    autoNextId: explanationIds.siteDrawAccept,
                    finished: false
                },
                {
                    title: 'Kavelgrens goedkeuren',
                    explanation: `Wis de selectie, ga één stapje terug of keur de selectie goed zodra u tevreden bent.`,
                    placement: 'left-end',
                    id: explanationIds.siteDrawAccept,
                    finished: false
                },
                {
                    title: 'Gebouw toevoegen',
                    explanation: `Hier kunt u een nieuw gebouw toevoegen.  U kunt er zoveel toevoegen als u wilt.`,
                    placement: 'left-end',
                    id: explanationIds.buildingAdd,
                    finished: false
                },
                {
                    title: 'Typologie selectie',
                    explanation: `Selecteer hier het gebouwtype dat u wenst toe te voegen aan uw kavel.`,
                    placement: 'bottom',
                    anchorId: 'building-types',
                    id: explanationIds.buildingSelectType,
                    finished: false
                },
                {
                    title: 'Woonmix instellen',
                    explanation: `Selecteer hier uw woonmix: de gewenste verhouding tussen de verschillende beschikbare woningen in het gebouw.`,
                    placement: 'bottom',
                    id: explanationIds.buildingLivingMix,
                    finished: false
                }, {
                    title: 'Navigatie',
                    explanation: `Hier kunt u tussen uw projecten, kavels en gebouwen navigeren of nieuwe  toevoegen, wijzigen of archiveren.`,
                    placement: 'left-end',
                    id: explanationIds.buildingNavigation,
                    autoNextId: explanationIds.buildingDetails,
                    finished: false
                }, {
                    title: 'Gebouw en projectgegevens',
                    explanation: `In het linkerpaneel kunt u naast de gerealiseerde woonmix ook de gegevens van het gebouw en project bekijken (oa. aantal woningen en bouwkosten)`,
                    placement: 'left-end',
                    id: explanationIds.buildingDetails,
                    autoNextId: explanationIds.buildingEdit,
                    finished: false
                }, {
                    title: 'Gebouw bewerken',
                    explanation: `In het rechterpaneel kunt u het door ons geplaatste gebouw bewerken door kolommen met woningen toe te voegen of te verwijderen of het aantal verdiepingen aan te passen.`,
                    placement: 'left-end',
                    id: explanationIds.buildingEdit,
                    finished: false
                }, {
                    title: 'Gebouw bewerken in 3D',
                    explanation: `Het gebouw kan vergroot en verkleind worden door aan de blauwe blokjes te trekken. Met de ‘gumball’ (vlak, pijlen en cirkel) kan het gebouw verplaatst en gedraaid worden. `,
                    placement: 'bottom-start',
                    anchorId: 'moos-logo',
                    id: explanationIds.buildingEdit3d,
                    finished: false
                }
            ]
        }
    ])


    const [currentStep, setCurrentStep] = useState<ExplanationStep>(undefined);
    const [currentExplanation, setCurrentExplanation] = useState<Explanation>(undefined);

    const [anchorEl, setAnchorEl] = useState<HTMLElement>(undefined);
    const [open, setOpen] = useState<boolean>(Boolean(anchorEl));

    useEffect(() => {
        if (showedExplanation) {
            if (!ftue?.explanationsSkipAll && !ftue?.explanationsFinished?.find(eid => eid === showedExplanation.id)) {
                const currentStep = explanations[0].steps.find(st => st.id === showedExplanation.id);
                const anchorEl = getAnchorEl(currentStep);
                if (anchorEl && !open) {
                    setOpen(true);
                    setCurrentStep(currentStep);
                    setAnchorEl(anchorEl);
                    setCurrentExplanation(explanations[0]);
                }
            } else {
                setOpen(false);
                setAnchorEl(undefined);
            }
        }
    }, [showedExplanation])

    useEffect(() => {
        if (ftue?.explanationsSkipAll) {
            const ex = explanations.map(e => ({ steps: e.steps.map(s => { s.finished = true; return { ...s }; }), name: e.name }));
            setExplanations(ex);
        } else if (ftue?.explanationsFinished?.length) {
            const ex = explanations.map(e => ({
                steps: e.steps.map(s => {
                    if (ftue.explanationsFinished.some(fid => s.id === fid)) {
                        s.finished = true;
                    }
                    return { ...s };
                }), name: e.name
            }));
            setExplanations(ex);
        }
    }, [])

    const update = () => {
        const currentExplanation = getCurrentExplanation(explanations);
        const currentStep = getCurrentStep(currentExplanation);
        const anchorEl = getAnchorEl(currentStep);

        if (anchorEl && !open) {
            setOpen(true);
            setAnchorEl(anchorEl);
            setCurrentStep(currentStep)
            setCurrentExplanation(currentExplanation);
        } else {
            setOpen(false);
            setAnchorEl(undefined);
        }
    }

    const skipAll = () => {
        const auth = getAuth();
        const path = `users/${auth.currentUser.email}/data`;

        FirestoreService.setDoc(path, {
            ...ftue,
            userId: auth.currentUser.uid,
            explanationsSkipAll: true,
        }, 'ftue', (err) => {
            handleError(err);
        });

        setFtue({
            ...ftue,
            explanationsSkipAll: true,
        })

        setOpen(false);
        setAnchorEl(undefined);
    }

    return <div className={styles.Explanations} data-testid="Explanations">

        <Bubble anchorEl={anchorEl} open={open} placement={currentStep?.placement}>
            <div className={styles.content}>
                <div className={styles.title}>
                    {currentStep && currentStep.title.toUpperCase()}
                </div>
                <div className={styles.explanation}>
                    {currentStep && currentStep.explanation}
                </div>
                <Button className={styles.skip} color="secondary" variant="outlined" size="small" onClick={skipAll}>Intro overslaan</Button>
                <Button className={styles.next} color="primary" variant="contained" size="small" onClick={(s) => {
                    const expl = finishCurrentStep(explanations);
                    setExplanations(expl);
                    let explanationsFinished = [currentStep.id];
                    if (ftue?.explanationsFinished?.length) {
                        explanationsFinished.push(...ftue.explanationsFinished)
                    }

                    setFtue({ ...ftue, explanationsFinished })

                    const auth = getAuth();
                    const path = `users/${auth.currentUser.email}/data`;

                    FirestoreService.setDoc(path, {
                        ...ftue,
                        userId: auth.currentUser.uid,
                        explanationsFinished,
                    }, 'ftue', (err) => {
                        handleError(err);
                    });
                    if (currentStep.autoNextId) {
                        setTimeout(() => {
                            ServiceManager.explanationService.showExplanation(currentStep.autoNextId);
                        }, 500)
                    }
                    update();
                }}><Check /></Button>
            </div>
        </Bubble>
    </div>
};

export default Explanations;
